import classNames from 'classnames'
import { ChevronDown } from 'lucide-react'
import { forwardRef, type SelectHTMLAttributes } from 'react'
import { Text } from './Text'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?:   string
  error?:   string
  options:  SelectOption[]
  placeholder?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select({ label, error, options, placeholder, disabled, className, id, ...props }, ref) {
    const selectId = id ?? label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className="flex flex-col gap-1.5 w-full">
        {label && (
          <Text variant="body-sm" as="label" {...({ htmlFor: selectId } as object)}>
            {label}
          </Text>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            className={classNames(
              'w-full h-11 pl-3 pr-9 appearance-none',
              'bg-surface border rounded-xl',
              'font-body text-base text-text-main',
              'transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-secondary/40',
              error
                ? 'border-red-500/50'
                : 'border-surface-alt hover:border-secondary/40',
              disabled && 'opacity-50 cursor-not-allowed',
              className,
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>{placeholder}</option>
            )}
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <ChevronDown
            size={16}
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-muted"
            aria-hidden="true"
          />
        </div>
        {error && (
          <Text variant="meta-md" tone="danger">{error}</Text>
        )}
      </div>
    )
  },
)

Select.displayName = 'Select'
